import React, { useCallback, useEffect, useRef, useState, useContext } from "react";
import { TableDropDown } from '@solsys/solsys-reactjs';
import { gql, useQuery, useMutation } from "@apollo/client";
import { NotificationContext } from "@solsys/solsys-reactjs";
import { useCreateIdempotencyKeys, mutationInput } from "../utils/idempotency";

const gqlQuery = gql`
  query tagDropDown($tag_list_request: tag_list_request) {
    tag(tag_list_request: $tag_list_request) {
      edges {
        id
        name
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation tagMutate($tag_mutation_request: MutationInput) {
    tag(tag_mutation_request: $tag_mutation_request) {
      id
      isNew
    }
  }
`;

const tag2ProspectMutation = gql`
  mutation tag2ProspectMutate($tag2Prospect_mutation_request: MutationInput) {
    tag2Prospect(tag2Prospect_mutation_request: $tag2Prospect_mutation_request) {
      id
      isNew
    }
  }
`;

const tag2CraneMutation = gql`
  mutation tag2CraneMutate($tag2Crane_mutation_request: MutationInput) {
    tag2Crane(tag2Crane_mutation_request: $tag2Crane_mutation_request) {
      id
      isNew
    }
  }
`;

const mapEdges = (edges) =>
  (edges ?? []).map((item) => ({
    ...item,
    ID: item.id,
    Name: item.name,
  }));

const TagDropDown = ({ selected, ...props }) => {
  selected = selected === -1 ? undefined : selected;

  const { addNotification } = useContext(NotificationContext);
  const truncateLength = 25;

  const [requestState, setRequestState] = useState({
    page: 1,
    pageSize: 20,
    search: "",
  });
  const { page, pageSize, search } = requestState;

  const [accumulatedList, setAccumulatedList] = useState([]);
  const filtersKey = JSON.stringify(props.filters ?? []);
  const sortingKey = JSON.stringify(props.sorting ?? []);
  const [newTagId, setNewTagId] = useState(0);
  const createKeys = useCreateIdempotencyKeys();
  const idempotencyKey = createKeys.keyFor("tag", String(search ?? ""));

  let variables = {
    tag_list_request: {
      pagination: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      filters: [],
      sorting: [
        {
          direction: "ASC",
          field: "id",
        },
      ],
    },
  };

  let renderVariables = {
    tag_list_request: {
      pagination: {
        limit: truncateLength,
        offset: 0,
      },
      filters: [],
      sorting: [
        {
          direction: "ASC",
          field: "id",
        },
      ],
    },
  };

  if (
    (Array.isArray(selected) && selected.length > 0) ||
    Number.isInteger(selected)
  ) {
    let array = [];
    if (props.multiple) {
      selected.forEach((v) => array.push(v));
    } else {
      array.push(selected);
    }
    renderVariables.tag_list_request.filters.push({
      field: "id",
      operator: "in",
      value: `(${array.join(", ")})`,
    });
  }

  if (search && search !== "")
    variables.tag_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${search}%`,
    });
  if (props.filters?.length > 0) {
    props.filters.forEach((f) =>
      variables.tag_list_request.filters.push(f)
    );
  }
  if (props.sorting?.length > 0) {
    props.sorting.forEach(
      (s) => (variables.tag_list_request.sorting = [s])
    );
  }

  let { refetch: renderRefetch, ...renderObject } = useQuery(gqlQuery, {
    variables: renderVariables,
  });
  const renderLoading = renderObject.loading;
  const renderError = renderObject.error;
  const renderData = renderObject.data;

  useEffect(() => {
    renderRefetch();
  }, [selected, renderRefetch]);

  const renderEntityListResponse = renderData?.tag;
  const renderEntityRaw = renderEntityListResponse?.edges ?? [];
  const renderEntity = renderEntityRaw.map(item => ({
    ...item,
    ID: item.id,
    Name: item.name
  }));

  const { refetch: refetchTag, loading, error, data } = useQuery(gqlQuery, {
    variables,
  });

  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      tag_mutation_request: mutationInput({
        properties: [
          { key: "name", string: search },
        ],
        idempotencyKey,
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      const createdTagId = response?.tag?.id;
      setNewTagId(createdTagId);

      if (props.multiple) {
        props.onSelect([...(selected || []), Number(createdTagId)]);
      } else {
        props.onSelect(Number(createdTagId));
      }

      if (props.entityType == 'prospect' && props.entityId) {
        tag2ProspectMutate();
      }
      if (props.entityType == 'crane' && props.entityId) {
        tag2CraneMutate();
      }

      setRequestState((s) => ({
        ...s,
        search: "",
        page: 1,
      }));
      setAccumulatedList([]);
      createKeys.resetAfterSuccess();
      addNotification("good", "Tag saved successfully");
    },
  });

  const [tag2ProspectMutate, tag2ProspectInfo] = useMutation(tag2ProspectMutation, {
    variables: {
      tag2Prospect_mutation_request: mutationInput({
        properties: [
          { key: "tag_id", number: Number(newTagId) },
          { key: "prospect_id", number: Number(props.entityId) },
        ],
        idempotencyKey: createKeys.keyFor(
          "tag2prospect",
          `${search}:${props.entityId}`
        ),
      }),
    },
    onError: (error) => {
      console.error(error);
    },
    onCompleted: (response, originalRequestVariables) => {
      props.onSelect([...selected, Number(newTagId)]);
    },
  });

  const [tag2CraneMutate, tag2CraneInfo] = useMutation(tag2CraneMutation, {
    variables: {
      tag2Crane_mutation_request: mutationInput({
        properties: [
          { key: "tag_id", number: Number(newTagId) },
          { key: "crane_id", number: Number(props.entityId) },
        ],
        idempotencyKey: createKeys.keyFor(
          "tag2crane",
          `${search}:${props.entityId}`
        ),
      }),
    },
    onError: (error) => {
      console.error(error);
    },
    onCompleted: (response, originalRequestVariables) => {
      props.onSelect([...selected, Number(newTagId)]);
    },
  });

  useEffect(() => {
    if (!props.disabled) {
      refetchTag();
    }
  }, [page, search, filtersKey, sortingKey, refetchTag, props.disabled]);

  useEffect(() => {
    setAccumulatedList([]);
    setRequestState((s) => ({ ...s, page: 1 }));
  }, [search, filtersKey, sortingKey]);

  useEffect(() => {
    if (loading) return;
    const edges = data?.tag?.edges;
    if (!edges) return;
    const mapped = mapEdges(edges);
    setAccumulatedList((prev) => {
      if (page === 1) return mapped;
      const seen = new Set(prev.map((x) => Number(x.id)));
      const toAdd = mapped.filter((x) => !seen.has(Number(x.id)));
      return [...prev, ...toAdd];
    });
  }, [data, page, loading]);

  const totalCountRef = useRef(0);
  totalCountRef.current = data?.tag?.totalCount ?? 0;
  const accumulatedLenRef = useRef(0);
  accumulatedLenRef.current = accumulatedList.length;

  const onSearch = (event) => {
    let { value } = event.target;
    setRequestState((s) => ({
      ...s,
      search: value,
      page: 1,
    }));
  };

  const paginationChange = (newPage) => {
    setRequestState((s) => ({
      ...s,
      page: newPage,
    }));
  };

  const addMoreResultsHandler = useCallback(() => {
    if (props.disabled) return;
    setRequestState((s) => {
      if (accumulatedLenRef.current >= totalCountRef.current) return s;
      return { ...s, page: s.page + 1 };
    });
  }, [props.disabled]);

  const createNew = () => {
    mutateFunction();
  }

  const entityCount = data?.tag?.totalCount ?? 0;

  if (error) console.error(`Tag drop down data error : ${error.message}`);
  if (renderError)
    console.error(`Tag drop down render error : ${renderError.message}`);

  const headers = [
    { label: "ID" },
    { label: "Name" }
  ];

  return (
    <TableDropDown
      { ...props }
      items={ accumulatedList }
      loading={ loading }
      renderLoading={ renderLoading }
      multiple={ props.multiple }
      searchEnabled={ props.searchEnabled }
      searchValue={ search }
      onSearch={ onSearch }
      createEnabled={ true }
      createNew={ createNew }
      selectValue={ selected }
      selectRender={ renderEntity }
      onSelect={ props.onChange || props.onSelect }
      dataKey={ "id" }
      identityColumnKey={ "ID" }
      renderColumnKey={ "Name" }
      headers={ headers }
      truncateLength={ truncateLength }
      entityCount={ entityCount }
      onPaginationChange={ paginationChange }
      addMoreResults={
        typeof props.addMoreResults === "function"
          ? props.addMoreResults
          : addMoreResultsHandler
      }
    />
  );
};

export default TagDropDown;
